import { useState, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { House, Sparkle, CalendarBlank, BookOpen, TrendUp, VideoCamera, ChartLine, CreditCard, UserCircle, SignOut, FileText, Target, List, X, Rocket } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';
import ThemeToggle from '@/components/ThemeToggle';
import PullToRefresh from '@/components/PullToRefresh';
import LanguageSelector from '@/components/LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Menu items with translation keys
const getMenuItems = (t) => [
  { icon: House, label: t('nav.dashboard'), path: '/dashboard' },
  { icon: Sparkle, label: t('nav.generate'), path: '/generate' },
  { icon: FileText, label: t('nav.seoPages'), path: '/seo-pages' },
  { icon: Target, label: t('nav.ranking'), path: '/ranking' },
  { icon: BookOpen, label: t('nav.library'), path: '/library' },
  { icon: CalendarBlank, label: t('nav.calendar', 'Calendrier'), path: '/calendar' },
  { icon: Rocket, label: t('nav.boost'), path: '/boost' },
  { icon: VideoCamera, label: t('nav.video'), path: '/video' },
  { icon: ChartLine, label: t('nav.stats'), path: '/stats' },
];

const getBottomItems = (t) => [
  { icon: CreditCard, label: t('nav.pricing'), path: '/pricing' },
  { icon: UserCircle, label: t('nav.profile'), path: '/profile' },
];

export default function AppLayout({ children }) {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useTheme();
  
  const menuItems = getMenuItems(t);
  const bottomItems = getBottomItems(t);

  const handleLogout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
      toast.success(t('auth.logoutSuccess'));
      navigate('/login');
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  const closeMobileMenu = () => setMobileMenuOpen(false);

  // Pull to refresh handler
  const handleRefresh = useCallback(async () => {
    // Simulate network delay for better UX
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Force re-render of children by updating key
    setRefreshKey(prev => prev + 1);
    
    // Show success toast
    toast.success(t('common.pageRefreshed'), { duration: 1500 });
    
    // Also reload data by triggering a navigation to same page
    // This will cause useEffect hooks to re-run
    window.dispatchEvent(new CustomEvent('pulltorefresh'));
  }, [t]);

  return (
    <div className={`flex min-h-screen ${isDark ? 'bg-[#0a0a14]' : 'bg-gray-50'}`}>
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex w-64 h-screen sticky top-0 flex-col transition-colors duration-200 ${
        isDark 
          ? 'bg-[#0d0d18] border-r border-white/[0.06]' 
          : 'bg-white border-r border-gray-200'
      }`}>
        <div className="p-5">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
              <Sparkle size={18} weight="fill" className="text-white" />
            </div>
            <div>
              <h1 className={`text-lg font-bold tracking-tight ${isDark ? 'text-white' : 'text-gray-900'}`}>
                SIMBLYO
              </h1>
              <p className={`text-[10px] ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                {t('nav.clientMachine')}
              </p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
          {menuItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`sidebar-${item.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-500/15 to-pink-500/10 text-violet-500'
                    : isDark 
                      ? 'text-white/50 hover:bg-white/[0.04] hover:text-white/80'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              <item.icon size={18} weight="duotone" />
              <span className="text-sm font-medium">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className={`p-3 space-y-0.5 ${isDark ? 'border-t border-white/[0.06]' : 'border-t border-gray-200'}`}>
          <div className={`flex items-center justify-between px-3 py-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            <span className="text-xs font-medium">{t('common.theme')}</span>
            <ThemeToggle />
          </div>
          
          <div className={`flex items-center justify-between px-3 py-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
            <span className="text-xs font-medium">{t('common.language')}</span>
            <LanguageSelector compact />
          </div>
          
          {bottomItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`sidebar-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150 ${
                  isActive
                    ? 'bg-gradient-to-r from-violet-500/15 to-pink-500/10 text-violet-500'
                    : isDark 
                      ? 'text-white/50 hover:bg-white/[0.04] hover:text-white/80'
                      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
                }`
              }
            >
              <item.icon size={18} weight="duotone" />
              <span className="text-sm font-medium">{item.label}</span>
            </NavLink>
          ))}
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout"
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-all duration-150 ${
              isDark 
                ? 'text-white/50 hover:bg-white/[0.04] hover:text-white/80'
                : 'text-gray-500 hover:bg-gray-100 hover:text-gray-900'
            }`}
          >
            <SignOut size={18} weight="duotone" />
            <span className="text-sm font-medium">{t('auth.logout')}</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50">
        <div className={`flex items-center justify-between px-4 py-3 backdrop-blur-xl ${
          isDark 
            ? 'bg-[#0a0a14]/90 border-b border-white/[0.06]' 
            : 'bg-white/90 border-b border-gray-200'
        }`}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-pink-500 flex items-center justify-center">
              <Sparkle size={16} weight="fill" className="text-white" />
            </div>
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              SIMBLYO
            </span>
          </div>
          <div className="flex items-center gap-3">
            <ThemeToggle className="scale-90" />
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/[0.06]' : 'hover:bg-gray-100'}`}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? (
                <X size={22} className={isDark ? 'text-white' : 'text-gray-900'} />
              ) : (
                <List size={22} className={isDark ? 'text-white' : 'text-gray-900'} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="md:hidden fixed inset-0 z-40 bg-black/50"
              onClick={closeMobileMenu}
            />
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={`md:hidden fixed right-0 top-0 bottom-0 z-50 w-72 overflow-y-auto ${
                isDark 
                  ? 'bg-[#0d0d18]' 
                  : 'bg-white'
              }`}
            >
              <div className="p-4 pt-16">
                <nav className="space-y-1">
                  {menuItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-violet-500/15 to-pink-500/10 text-violet-500'
                            : isDark 
                              ? 'text-white/60 hover:bg-white/[0.04]'
                              : 'text-gray-600 hover:bg-gray-100'
                        }`
                      }
                    >
                      <item.icon size={20} weight="duotone" />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  ))}
                </nav>

                <div className={`mt-6 pt-6 space-y-1 ${isDark ? 'border-t border-white/[0.06]' : 'border-t border-gray-200'}`}>
                  {bottomItems.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                          isActive
                            ? 'bg-gradient-to-r from-violet-500/15 to-pink-500/10 text-violet-500'
                            : isDark 
                              ? 'text-white/60 hover:bg-white/[0.04]'
                              : 'text-gray-600 hover:bg-gray-100'
                        }`
                      }
                    >
                      <item.icon size={20} weight="duotone" />
                      <span className="font-medium">{item.label}</span>
                    </NavLink>
                  ))}
                  <button
                    onClick={() => { closeMobileMenu(); handleLogout(); }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${
                      isDark 
                        ? 'text-white/60 hover:bg-white/[0.04]'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    <SignOut size={20} weight="duotone" />
                    <span className="font-medium">{t('auth.logout')}</span>
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 md:pt-0 pt-14 overflow-hidden">
        <PullToRefresh onRefresh={handleRefresh}>
          <div key={refreshKey} className="min-h-full">
            {children}
          </div>
        </PullToRefresh>
      </main>
    </div>
  );
}
